import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PricingSection = () => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [billingCycle, setBillingCycle] = useState('annual');
  const [selectedPlan, setSelectedPlan] = useState('professional');

  const translations = {
    en: {
      title: "Choose Your Perfect Plan",
      subtitle: "Transparent pricing with no hidden costs. All plans include GST and come with 24/7 support",
      billingToggle: {
        monthly: "Monthly",
        annual: "Annual (Save 20%)"
      },
      plans: {
        starter: {
          name: "Starter",
          description: "Perfect for small aluminum manufacturers getting started with ERP",
          monthlyPrice: 15000,
          annualPrice: 144000,
          features: [
            "Up to 25 users",
            "Basic production planning",
            "Order management",
            "Inventory tracking",
            "Basic reporting",
            "Email support",
            "Mobile app access"
          ],
          limitations: [
            "Limited to 1 location",
            "Basic integrations only"
          ]
        },
        professional: {
          name: "Professional",
          description: "Most popular choice for growing aluminum extrusion businesses",
          monthlyPrice: 35000,
          annualPrice: 336000,
          features: [
            "Up to 100 users",
            "Advanced production planning",
            "Complete sales management",
            "Quality control module",
            "Advanced analytics",
            "Priority phone support",
            "API integrations",
            "Multi-location support"
          ],
          popular: true
        },
        enterprise: {
          name: "Enterprise",
          description: "Comprehensive solution for large-scale aluminum manufacturing operations",
          monthlyPrice: 75000,
          annualPrice: 720000,
          features: [
            "Unlimited users",
            "Full ERP suite",
            "Custom workflows",
            "Advanced quality control",
            "Real-time dashboards",
            "Dedicated account manager",
            "Custom integrations",
            "On-site training",
            "24/7 priority support"
          ]
        }
      },
      features: {
        implementation: "Implementation & Training",
        support: "24/7 Support",
        updates: "Free Updates",
        backup: "Daily Backups",
        security: "Enterprise Security"
      },
      cta: {
        getStarted: "Get Started",
        contactSales: "Contact Sales",
        mostPopular: "Most Popular"
      },
      gstNote: "All prices include 18% GST",
      moneyBack: "30-day money-back guarantee"
    },
    hi: {
      title: "अपना सही प्लान चुनें",
      subtitle: "बिना छुपी लागत के पारदर्शी मूल्य निर्धारण। सभी प्लान में GST शामिल है और 24/7 सहायता मिलती है",
      billingToggle: {
        monthly: "मासिक",
        annual: "वार्षिक (20% बचत)"
      },
      plans: {
        starter: {
          name: "स्टार्टर",
          description: "ERP के साथ शुरुआत करने वाले छोटे एल्यूमीनियम निर्माताओं के लिए बिल्कुल सही",
          monthlyPrice: 15000,
          annualPrice: 144000,
          features: [
            "25 उपयोगकर्ता तक",
            "बेसिक उत्पादन योजना",
            "ऑर्डर प्रबंधन",
            "इन्वेंटरी ट्रैकिंग",
            "बेसिक रिपोर्टिंग",
            "ईमेल सहायता",
            "मोबाइल ऐप एक्सेस"
          ],
          limitations: [
            "1 स्थान तक सीमित",
            "केवल बेसिक इंटीग्रेशन"
          ]
        },
        professional: {
          name: "प्रोफेशनल",
          description: "बढ़ते एल्यूमीनियम एक्सट्रूज़न व्यवसायों के लिए सबसे लोकप्रिय विकल्प",
          monthlyPrice: 35000,
          annualPrice: 336000,
          features: [
            "100 उपयोगकर्ता तक",
            "उन्नत उत्पादन योजना",
            "संपूर्ण बिक्री प्रबंधन",
            "गुणवत्ता नियंत्रण मॉड्यूल",
            "उन्नत विश्लेषण",
            "प्राथमिकता फोन सहायता",
            "API इंटीग्रेशन",
            "मल्टी-लोकेशन सहायता"
          ],
          popular: true
        },
        enterprise: {
          name: "एंटरप्राइज",
          description: "बड़े पैमाने के एल्यूमीनियम निर्माण संचालन के लिए व्यापक समाधान",
          monthlyPrice: 75000,
          annualPrice: 720000,
          features: [
            "असीमित उपयोगकर्ता",
            "पूर्ण ERP सूट",
            "कस्टम वर्कफ़्लो",
            "उन्नत गुणवत्ता नियंत्रण",
            "रीयल-टाइम डैशबोर्ड",
            "समर्पित खाता प्रबंधक",
            "कस्टम इंटीग्रेशन",
            "ऑन-साइट प्रशिक्षण",
            "24/7 प्राथमिकता सहायता"
          ]
        }
      },
      features: {
        implementation: "कार्यान्वयन और प्रशिक्षण",
        support: "24/7 सहायता",
        updates: "मुफ्त अपडेट",
        backup: "दैनिक बैकअप",
        security: "एंटरप्राइज सुरक्षा"
      },
      cta: {
        getStarted: "शुरू करें",
        contactSales: "बिक्री से संपर्क करें",
        mostPopular: "सबसे लोकप्रिय"
      },
      gstNote: "सभी कीमतों में 18% GST शामिल है",
      moneyBack: "30-दिन पैसे वापसी की गारंटी"
    }
  };

  const t = translations[currentLanguage];

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language');
    if (savedLanguage) {
      setCurrentLanguage(savedLanguage);
    }
  }, []);

  const formatPrice = (price) => {
    if (price >= 100000) {
      return `₹${(price / 100000).toFixed(0)}L`;
    }
    return `₹${price.toLocaleString()}`;
  };

  const getPrice = (plan) => {
    return billingCycle === 'annual' ? plan.annualPrice : plan.monthlyPrice;
  };

  const getMonthlyPrice = (plan) => {
    return billingCycle === 'annual' ? Math.floor(plan.annualPrice / 12) : plan.monthlyPrice;
  };

  return (
    <section id="pricing" className="py-20 bg-surface-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6">
            {t.title}
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto mb-8">
            {t.subtitle}
          </p>

          {/* Billing Toggle */}
          <div className="inline-flex bg-white rounded-xl p-2 shadow-sm border border-border">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
                billingCycle === 'monthly' ?'bg-primary text-white shadow-sm' :'text-text-secondary hover:text-primary'
              }`}
            >
              {t.billingToggle.monthly}
            </button>
            <button
              onClick={() => setBillingCycle('annual')}
              className={`px-6 py-3 rounded-lg font-medium transition-all duration-300 relative ${
                billingCycle === 'annual' ?'bg-primary text-white shadow-sm' :'text-text-secondary hover:text-primary'
              }`}
            >
              {t.billingToggle.annual}
              <span className="absolute -top-2 -right-2 bg-accent text-white text-xs px-2 py-1 rounded-full">
                20%
              </span>
            </button>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {Object.entries(t.plans).map(([key, plan]) => (
            <div
              key={key}
              className={`relative bg-white rounded-2xl shadow-lg border-2 transition-all duration-300 hover:shadow-xl ${
                plan.popular
                  ? 'border-primary scale-105'
                  : selectedPlan === key
                  ? 'border-accent' :'border-border hover:border-primary'
              }`}
            >
              {/* Popular Badge */}
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <div className="bg-gradient-accent text-white px-4 py-2 rounded-full text-sm font-medium shadow-lg">
                    {t.cta.mostPopular}
                  </div>
                </div>
              )}

              <div className="p-8">
                {/* Plan Header */}
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-primary mb-2">
                    {plan.name}
                  </h3>
                  <p className="text-text-secondary text-sm mb-6">
                    {plan.description}
                  </p>
                  
                  {/* Price */}
                  <div className="space-y-2">
                    <div className="flex items-baseline justify-center space-x-2">
                      <span className="text-4xl font-bold text-primary">
                        {formatPrice(getMonthlyPrice(plan))}
                      </span>
                      <span className="text-text-secondary">
                        /{currentLanguage === 'en' ? 'month' : 'महीना'}
                      </span>
                    </div>
                    
                    {billingCycle === 'annual' && (
                      <div className="text-sm text-text-muted">
                        {formatPrice(getPrice(plan))} {currentLanguage === 'en' ? 'billed annually' : 'वार्षिक बिल'}
                      </div>
                    )}
                  </div>
                </div>

                {/* Features */}
                <div className="space-y-4 mb-8">
                  {plan.features.map((feature, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <Icon 
                        name="CheckCircle" 
                        size={20} 
                        color="var(--color-success)" 
                        className="mt-0.5 flex-shrink-0"
                      />
                      <span className="text-text-secondary text-sm">{feature}</span>
                    </div>
                  ))}
                  
                  {plan.limitations && plan.limitations.map((limitation, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <Icon 
                        name="X" 
                        size={20} 
                        color="var(--color-text-muted)" 
                        className="mt-0.5 flex-shrink-0"
                      />
                      <span className="text-text-muted text-sm">{limitation}</span>
                    </div>
                  ))}
                </div>

                {/* CTA Button */}
                <Button
                  variant={plan.popular ? "primary" : "outline"}
                  size="lg"
                  onClick={() => setSelectedPlan(key)}
                  className={`w-full ${
                    plan.popular 
                      ? 'bg-gradient-accent hover:bg-accent-600 shadow-cta' 
                      : 'border-primary text-primary hover:bg-primary-50'
                  }`}
                  iconName={key === 'enterprise' ? "Phone" : "ArrowRight"}
                  iconPosition="right"
                >
                  {key === 'enterprise' ? t.cta.contactSales : t.cta.getStarted}
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Additional Features */}
        <div className="bg-white rounded-2xl p-8 shadow-lg border border-border">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-primary mb-4">
              {currentLanguage === 'en' ? 'All Plans Include' : 'सभी प्लान में शामिल'}
            </h3>
          </div>
          
          <div className="grid md:grid-cols-5 gap-6">
            {Object.entries(t.features).map(([key, feature]) => (
              <div key={key} className="text-center">
                <div className="flex items-center justify-center w-16 h-16 bg-primary-50 rounded-xl mx-auto mb-4">
                  <Icon 
                    name={
                      key === 'implementation' ? 'Settings' :
                      key === 'support' ? 'Headphones' :
                      key === 'updates' ? 'Download' :
                      key === 'backup'? 'Database' : 'Shield'
                    } 
                    size={32} 
                    color="var(--color-primary)" 
                  />
                </div>
                <h4 className="font-semibold text-primary mb-2">{feature}</h4>
              </div>
            ))}
          </div>
        </div>

        {/* Footer Notes */}
        <div className="text-center mt-12 space-y-4">
          <p className="text-text-secondary">
            {t.gstNote}
          </p>
          <div className="flex items-center justify-center space-x-2">
            <Icon name="Shield" size={16} color="var(--color-success)" />
            <span className="text-success text-sm font-medium">
              {t.moneyBack}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;